import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import {
  BloodworkAnalysis,
  RootCauseCategory,
  FurtherConsiderations,
  Section,
} from '../../models';
import { stripSupplementUrl } from '../../utils/supplementItem';

// The DB authors up/down status as the Unicode triangles ▲ / ▼ in the
// Interpretation and Causes text (e.g. "Logic: ▼ WBC and ▲ NLR"). Swap those
// for Font Awesome arrow icons at render time so the stored data stays
// plain-text while the report shows real arrows. Note: the app is on Font
// Awesome 5 via the React SVG component (@fortawesome/react-fontawesome) — no
// web-font CSS is loaded, so raw `<i class="fa-…">` markup would render
// nothing; FontAwesomeIcon is the supported path here.
const ARROW_ICON_STYLE: React.CSSProperties = {
  fontSize: '1.1em',
  verticalAlign: '-0.1em',
};

// Turn the literal ▲ / ▼ markers embedded in consideration titles and items
// into styled FontAwesome up/down arrows. Exported so the Notes "gather" digest
// renders the same arrows the tab does instead of raw triangle characters.
export function renderArrows(
  text: string,
  keyPrefix: string | number,
): React.ReactNode {
  if (!text || (text.indexOf('▲') === -1 && text.indexOf('▼') === -1)) {
    return text;
  }
  return text.split(/([▲▼])/).map((part, i) => {
    if (part === '▲') {
      return (
        <FontAwesomeIcon
          key={`${keyPrefix}-up-${i}`}
          icon={faArrowUp}
          style={ARROW_ICON_STYLE}
        />
      );
    }
    if (part === '▼') {
      return (
        <FontAwesomeIcon
          key={`${keyPrefix}-down-${i}`}
          icon={faArrowDown}
          style={ARROW_ICON_STYLE}
        />
      );
    }
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>;
  });
}

// References bullets end with "PMID: 12345678" or "PMID: 12345678, 23456789, …".
// Linkify each PMID number to PubMed so practitioners can jump straight to the
// citation. Sheet cells already hyperlink them, but the gviz CSV export strips
// hyperlinks, so we reconstruct deterministically on the client.
const PMID_PREFIX_RE = /\bPMID:\s*/i;

// A pattern's References row (subId 4 / 5) now stores Reference Library tag
// slugs in items[], not full citations. A tag is letters (either case),
// digits, and underscores — anything else (has spaces/punctuation) is treated
// as a legacy inline citation and falls through to the original PMID-linkifier.
// Case is allowed both ways so PRC-library tags (e.g. NEUT_1, ZINC_1) match the
// spreadsheet's uppercase convention while legacy Glucose tags stay lowercase.
const TAG_RE = /^[a-zA-Z0-9_]+$/;

// Parses a Reference Library row's combined items[0] back into its parts.
// pullGlucoseReferenceLibrary.js builds it deterministically as:
//   "<citation> PMID: <num> <url>"
//   "<citation> Bookshelf ID: NBK<num> <url>"
//   "<citation> <url>"          (no identifier)
//   "<citation>"                (citation only)
// Parse from the END so the citation (which may contain digits or colons)
// is whatever's left over.
function parseLibraryCitation(combined: string): {
  citation: string;
  identifier?: string;
  identifierNumber?: string;
  url?: string;
} {
  let working = combined.trim();
  let url: string | undefined;
  const urlMatch = working.match(/\s+(https?:\/\/\S+)\s*$/);
  if (urlMatch) {
    url = urlMatch[1];
    working = working.slice(0, urlMatch.index).trimEnd();
  }
  let identifier: string | undefined;
  let identifierNumber: string | undefined;
  const pmidMatch = working.match(/\s+PMID:\s*(\d+)\s*$/i);
  const bookshelfMatch = working.match(/\s+Bookshelf ID:\s*(NBK\d+)\s*$/i);
  if (pmidMatch) {
    identifier = `PMID: ${pmidMatch[1]}`;
    identifierNumber = pmidMatch[1];
    working = working.slice(0, pmidMatch.index).trimEnd();
  } else if (bookshelfMatch) {
    identifier = `Bookshelf ID: ${bookshelfMatch[1]}`;
    identifierNumber = bookshelfMatch[1];
    working = working.slice(0, bookshelfMatch.index).trimEnd();
  }
  return { citation: working, identifier, identifierNumber, url };
}

// Old format: items[] contained full citation strings. Linkify any PMID
// numbers and render the rest as plain text. Kept as a fallback for any
// References row that hasn't been migrated to tag-based items.
function renderLegacyCitation(item: string): JSX.Element {
  const match = PMID_PREFIX_RE.exec(item);
  if (!match) return <>{item}</>;
  const splitAt = match.index + match[0].length;
  const before = item.slice(0, splitAt);
  const after = item.slice(splitAt);
  const parts = after.split(/(\d+)/);
  return (
    <>
      {before}
      {parts.map((part, idx) =>
        /^\d+$/.test(part) ? (
          <a
            key={idx}
            href={`https://pubmed.ncbi.nlm.nih.gov/${part}/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        ) : (
          <React.Fragment key={idx}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

// PRC-style reference tags are "<PREFIX>_<n>" (e.g. NEUT_1, ZINC_6). That <n>
// mirrors the inline "(n)" citation markers in the Possible Root Cause text, so
// we surface it in the References list too. Glucose-style slug tags
// (e.g. ada_diagnosis_..._2026) have no such number and return null.
function referenceNumber(item: string): string | null {
  const m = /^[A-Za-z]+_(\d+)$/.exec(item);
  return m ? m[1] : null;
}

// New format: items[] holds Reference Library tag slugs. Resolve each to its
// library row and render "(n) <citation>  —  <identifier>" where the identifier
// (PMID or Bookshelf ID) links to the source URL, and the leading "(n)" (when
// present) matches the inline citation markers. Falls back to the legacy
// renderer for non-tag items so non-migrated sections keep working.
function renderReferenceItem(
  item: string,
  furtherConsiderations: FurtherConsiderations,
): JSX.Element {
  const num = referenceNumber(item);
  const prefix = num ? `(${num}) ` : '';
  if (!TAG_RE.test(item)) {
    return (
      <>
        {prefix}
        {renderLegacyCitation(item)}
      </>
    );
  }
  const libraryRow = furtherConsiderations.findByTag(item);
  if (!libraryRow || !libraryRow.items || libraryRow.items.length === 0) {
    // Unknown tag — surface the raw slug so a missing library row is visible
    // rather than silently empty.
    return (
      <>
        {prefix}
        {item}
      </>
    );
  }
  const { citation, identifier, url } = parseLibraryCitation(libraryRow.items[0]);
  if (!identifier) {
    // No PMID / Bookshelf ID — render citation, optionally followed by the URL.
    return (
      <>
        {prefix}
        {citation}
        {url ? (
          <>
            {'  —  '}
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          </>
        ) : null}
      </>
    );
  }
  return (
    <>
      {prefix}
      {citation}
      {'  —  '}
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {identifier}
        </a>
      ) : (
        <>{identifier}</>
      )}
    </>
  );
}

// Plain-text form of a References item for the Notes "gather" digest, mirroring
// renderReferenceItem: "(n) <citation> — <identifier|url>". Resolves a library
// tag slug via findByTag; falls back to the raw string for legacy/unknown items.
// So the digest's References card reads like the tab (and gives the "(n)" markers
// in the Possible Root Causes text a home) as plain text that inserts cleanly.
export function referenceItemToText(
  item: string,
  furtherConsiderations: FurtherConsiderations,
): string {
  const num = referenceNumber(item);
  const prefix = num ? `(${num}) ` : '';
  if (!TAG_RE.test(item)) {
    return `${prefix}${item}`;
  }
  const libraryRow = furtherConsiderations.findByTag(item);
  if (!libraryRow || !libraryRow.items || libraryRow.items.length === 0) {
    return `${prefix}${item}`;
  }
  const { citation, identifier, url } = parseLibraryCitation(libraryRow.items[0]);
  let out = `${prefix}${citation}`;
  if (identifier) out += ` — ${identifier}`;
  else if (url) out += ` — ${url}`;
  return out;
}

// Build a { "<n>": url } map for a marker so inline "(n)" citation markers in
// its Causes/Symptoms text can link out. The number matches a tag's numeric
// suffix (e.g. "(6)" → tag ZINC_6 → its library URL), read from the marker's
// References row (subCategoryId 4) and resolved via findByTag.
function buildCitationUrlMap(
  rootCauseCategory: number,
  section: string,
  furtherConsiderations: FurtherConsiderations,
): { [n: string]: string } {
  const map: { [n: string]: string } = {};
  const refRows = furtherConsiderations.findConsiderations(
    rootCauseCategory,
    section,
    'References',
  );
  for (const row of refRows) {
    for (const tag of row.items || []) {
      const suffix = /_(\d+)$/.exec(tag);
      if (!suffix) continue;
      const lib = furtherConsiderations.findByTag(tag);
      if (!lib || !lib.items || !lib.items.length) continue;
      const { url } = parseLibraryCitation(lib.items[0]);
      if (url) map[suffix[1]] = url;
    }
  }
  return map;
}

// Render a plain-text item, turning inline "(n)" markers into links to the
// matching reference URL. Numbers with no known URL stay as plain text, so
// sections without a References row are unaffected.
function renderCitedText(
  item: string,
  urlMap: { [n: string]: string },
): JSX.Element | string {
  if (!urlMap || Object.keys(urlMap).length === 0) {
    return <>{renderArrows(item, 'arr')}</>;
  }
  // Split out citation groups — a single "(6)" or a multi like "(3, 5)".
  const parts = item.split(/(\((?:\d+\s*,\s*)*\d+\))/g);
  return (
    <>
      {parts.map((part, i) => {
        const group = /^\(((?:\d+\s*,\s*)*\d+)\)$/.exec(part);
        if (!group) {
          return <React.Fragment key={i}>{renderArrows(part, i)}</React.Fragment>;
        }
        const nums = group[1].split(/\s*,\s*/);
        // Leave untouched if none of the numbers resolve to a reference.
        if (!nums.some(n => urlMap[n])) {
          return <React.Fragment key={i}>{part}</React.Fragment>;
        }
        // Keep the parens/commas as text; link each number (bold, so it's
        // clearly clickable) to its own reference.
        return (
          <React.Fragment key={i}>
            {'('}
            {nums.map((n, j) => (
              <React.Fragment key={j}>
                {j > 0 ? ', ' : ''}
                {urlMap[n] ? (
                  <a
                    href={urlMap[n]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 'bold' }}
                  >
                    {n}
                  </a>
                ) : (
                  n
                )}
              </React.Fragment>
            ))}
            {')'}
          </React.Fragment>
        );
      })}
    </>
  );
}

function renderSupplementItem(
  item: string,
  furtherConsiderations: FurtherConsiderations,
): JSX.Element {
  // resolveSupplement handles both forms: a supplement-library tag (resolved
  // via findByTag) and a legacy inline "Name — Description — url" string.
  const parsed = furtherConsiderations.resolveSupplement(item);
  if (parsed) {
    const { name, description, link } = parsed;
    const nameNode = link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontWeight: 'bold' }}
      >
        {name}
      </a>
    ) : (
      <strong>{name}</strong>
    );
    return (
      <>
        {nameNode}
        {description ? <> — {description}</> : null}
      </>
    );
  }
  // Promo lines and other unstructured items fall through unchanged, but strip
  // any trailing URL so it never leaks as raw text.
  return <>{stripSupplementUrl(item).text}</>;
}

// Rule: a Supplements row that resolves to exactly ONE supplement reads
// "Supporting Supplement for …" (singular); two or more keep the stored plural
// "Supplements". Applied at render so titles can stay canonically plural in the
// data/sheet. Promo and other unstructured bullets (e.g. the "Use code …" line)
// don't resolve to a supplement, so they never count toward the total.
function supplementTitle(
  title: string,
  items: string[],
  furtherConsiderations: FurtherConsiderations,
): string {
  const supplementCount = (items || []).filter(
    item => furtherConsiderations.resolveSupplement(item) !== null,
  ).length;
  return supplementCount === 1
    ? title.replace(/\bSupplements\b/, 'Supplement')
    : title;
}

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  section: Section;
  tab: string;
  brands?: string[];
};

// WBC: float the 110xx “Immune System Abnormality …” drivers to the top.
// Glucose: float the 710xx Glucose Dysregulation Likelihood drivers to the top —
// same pattern, so the gauge's "why this %" reads first. Other sections keep
// their original order. Exported so the Notes "gather" digest can present each
// tab's considerations in the SAME order the tab itself shows them.
export function prioritizeRootCauseCategories<T extends { rootCauseCategory: RootCauseCategory }>(
  section: Section,
  categories: T[],
): T[] {
  const floatRange = (lo: number, hi: number) =>
    categories
      .map((c, idx) => {
        const id = Number(c.rootCauseCategory);
        const inRange = id >= lo && id <= hi;
        return { c, idx, id, inRange };
      })
      .sort((a, b) => {
        if (a.inRange !== b.inRange) return a.inRange ? -1 : 1; // in-range group first
        if (a.inRange && b.inRange) return b.id - a.id; // within it, higher tier first
        return a.idx - b.idx; // everything else keeps original order
      })
      .map(x => x.c);

  if (section === Section.WBC) return floatRange(11010, 11099);
  if (section === Section.GLUCOSE) return floatRange(71000, 71099);
  return categories;
}

export class FurtherConsiderationsSection extends React.Component<Props, {}> {
  furtherConsiderationsForPossibleRootCauses(
    bloodworkAnalysis: BloodworkAnalysis,
  ): JSX.Element {
    const { section } = this.props;

    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor(section) || [];
    const prioritized = prioritizeRootCauseCategories(section, categories);

    return (
      <span>
        {prioritized.map(category => {
          return this.furtherConsiderationsFor(category.rootCauseCategory);
        })}
      </span>
    );
  }
  furtherConsiderationsFor(rootCauseCategory: RootCauseCategory): JSX.Element {
    const { furtherConsiderations, section, tab, brands } = this.props;
    // Inline "(n)" citation markers (in Causes/Symptoms text) link to the
    // marker's References; skipped for tabs that have their own renderers.
    const citationUrlMap =
      tab === 'Supplements' || tab === 'References'
        ? {}
        : buildCitationUrlMap(rootCauseCategory, section, furtherConsiderations);
    return (
      <span key={rootCauseCategory}>
        {
          // tslint:disable-next-line:max-line-length
          furtherConsiderations
            .findConsiderations(rootCauseCategory, section, tab, brands)
            .map((consideration, cindex) => {
              return (
                <span key={cindex}>
                  <span key={`title-${cindex}`} className="consideration-title">
                    {renderArrows(
                      tab === 'Supplements'
                        ? supplementTitle(
                            consideration.title,
                            consideration.items,
                            furtherConsiderations,
                          )
                        : consideration.title,
                      `title-${cindex}`,
                    )}
                  </span>
                  <ul>
                    {consideration.items.map((item, itemIndex) => {
                      return (
                        <li
                          key={`${rootCauseCategory}-${cindex}-${itemIndex}`}
                          // Numbered references show their "(n)" instead of a
                          // bullet, matching the inline citation markers.
                          style={
                            tab === 'References' && referenceNumber(item)
                              ? { listStyleType: 'none' }
                              : undefined
                          }
                        >
                          {tab === 'Supplements'
                            ? renderSupplementItem(item, furtherConsiderations)
                            : tab === 'References'
                            ? renderReferenceItem(item, furtherConsiderations)
                            : renderCitedText(item, citationUrlMap)}
                        </li>
                      );
                    })}
                  </ul>
                </span>
              );
            })
        }
      </span>
    );
  }

  render() {
    const { bloodworkAnalysis } = this.props;

    if (!bloodworkAnalysis.bloodwork) {
      return null;
    }

    return this.furtherConsiderationsForPossibleRootCauses(bloodworkAnalysis);
  }
}
